import { useOrg } from "@/contexts/OrgContext";
import { useCompanyInfoFull } from "@/hooks/useCompanyInfo";
import { useTenantOrgId } from "@/hooks/useTenantOrgId";
import { PlatformPrivacy } from "@/components/legal/PlatformPrivacy";
import { PlatformTerms } from "@/components/legal/PlatformTerms";
import { CookiePolicyContent } from "@/components/legal/CookiePolicyContent";
import { TenantPrivacy, TenantTerms, type TenantLegalProps } from "@/components/legal/TenantPolicies";

/**
 * Legal pages (/privacy, /terms, /cookies).
 *
 * - Marketing site (apex): The Polished Version's own Privacy Policy, Terms and
 *   Conditions (for businesses using the platform) and Cookie Policy.
 * - A business's website (subdomain / custom domain): a customer-facing privacy
 *   notice and website terms in the business's name — the business is the
 *   controller for its customers — each linking back to The Polished Version's
 *   own policy. The Cookie Policy is the same app's storage, named for the business.
 *
 * Content lives in src/components/legal; legal identity details in src/config/legal.ts.
 */

/** The tenant's business name (company_info, falling back to the org name) and phone, or null on the marketing site. */
function useTenantLegalDetails(): TenantLegalProps | null {
  const { isMarketingSite, org } = useOrg();
  const orgId = useTenantOrgId();
  const { data: info } = useCompanyInfoFull(isMarketingSite ? undefined : orgId);
  if (isMarketingSite) return null;
  const businessName = info?.company_name?.trim() || org.name?.trim() || "This business";
  return { businessName, phone: info?.phone_number?.trim() || null };
}

export function PrivacyPolicy() {
  const tenant = useTenantLegalDetails();
  return tenant ? <TenantPrivacy {...tenant} /> : <PlatformPrivacy />;
}

export function TermsAndConditions() {
  const tenant = useTenantLegalDetails();
  return tenant ? <TenantTerms {...tenant} /> : <PlatformTerms />;
}

export function CookiePolicy() {
  const tenant = useTenantLegalDetails();
  return <CookiePolicyContent businessName={tenant?.businessName} />;
}
